import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cross, X, Send } from "lucide-react";
import { API } from "@/lib/api";

const SUGGESTIONS = [
  "O que é a DACB?",
  "Horários de missa na Sé Primacial",
  "Como enviar um pedido de oração?",
  "Como encontrar uma comunidade?",
];

function getSessionId() {
  let id = localStorage.getItem("dacb_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("dacb_chat_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: "Paz e bem! Sou o assistente pastoral da DACB. Posso ajudar com dúvidas sobre a diocese, a fé católico-anglicana, sacramentos, horários e comunidades. Como posso servir você?",
    },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setSending(true);
    setMessages((prev) => [...prev, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: msg }),
      });
      if (!res.ok) throw new Error("Erro " + res.status);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        const partial = acc;
        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: partial };
          return copy;
        });
      }
    } catch {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "No momento não consegui responder. Tente novamente em instantes ou fale conosco pelo WhatsApp Pastoral.",
        };
        return copy;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <motion.button
        data-testid="chat-widget-button"
        onClick={() => setOpen((v) => !v)}
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        aria-label="Abrir assistente pastoral"
        className="fixed bottom-6 right-6 z-[95] flex h-14 w-14 items-center justify-center rounded-full bg-[#C5A059] text-[#2E070D] shadow-[0_10px_30px_rgba(46,7,13,0.35)] transition-colors hover:bg-[#D4AF37]"
      >
        {open ? <X className="h-6 w-6" /> : <Cross className="h-6 w-6" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-4 sm:right-6 z-[95] flex h-[520px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden border border-[#C5A059]/40 bg-[#FBF9F4] shadow-[0_24px_60px_rgba(46,7,13,0.35)]"
          >
            <div className="flex items-center gap-3 bg-[#2E070D] px-4 py-3.5 text-[#FBF9F4]">
              <img src="/brasao-dacb-oficial.webp" alt="Brasão oficial da Diocese Anglicana Católica do Brasil" className="h-9 w-auto object-contain" />
              <div>
                <p className="font-display text-sm font-bold">Capelania Digital</p>
                <p className="overline-label text-[#C5A059]">Assistente pastoral • DACB</p>
              </div>
            </div>

            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div
                  key={i}
                  data-testid={`chat-message-${m.role}-${i}`}
                  className={`max-w-[85%] px-4 py-3 text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user"
                      ? "ml-auto bg-[#4A0E17] text-[#FBF9F4]"
                      : "mr-auto border border-[#E5DEC9] bg-white text-[#1C1917]"
                  }`}
                >
                  {m.content.replace(/\*\*/g, "") || (sending && i === messages.length - 1 ? <span className="animate-pulse-soft text-[#C5A059]">●●●</span> : m.content)}
                </div>
              ))}
              {messages.length === 1 && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {SUGGESTIONS.map((s) => (
                    <button
                      key={s}
                      data-testid={`chat-suggestion-${s.slice(0, 12).replace(/\W/g, "-")}`}
                      onClick={() => send(s)}
                      className="overline-label border border-[#C5A059]/50 px-3 py-2 text-[#4A0E17] transition-colors hover:bg-[#4A0E17] hover:text-[#FBF9F4]"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="flex items-center gap-2 border-t border-[#E5DEC9] bg-white px-3 py-3"
            >
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Escreva sua pergunta..."
                maxLength={2000}
                className="flex-1 bg-transparent text-sm text-[#1C1917] placeholder-[#1C1917]/40 focus:outline-none"
              />
              <button
                data-testid="chat-send-button"
                type="submit"
                disabled={sending || !input.trim()}
                aria-label="Enviar mensagem"
                className="flex h-9 w-9 items-center justify-center bg-[#C5A059] text-[#2E070D] transition-colors hover:bg-[#D4AF37] disabled:opacity-40"
              >
                <Send className="h-4 w-4" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
